import { Panel, PanelType } from "@fluentui/react/lib/Panel";
import { ExperimentType, VariantDetail } from "../../../../Types";
import {
  Label,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Toggle,
} from "@fluentui/react";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import {
  BaseTextField,
  BaseKeyValueField,
  FormRow,
} from "../../FormComponents";
import { useCallback, useMemo, useRef, useState } from "react";
import { styles } from "../variantDetailsFormStyles";
import { styles as sharedStyles } from "../../../../Shared/shared.styles";
import { injectWiggleAnimation } from "../../../../Shared/shared.styles";
import { featureFlagRenderer } from "../VariantDetailsFormTable/variantDetailsFormTableColumnRenderers";
import { variantDetailNameValidator } from "../../ExperimentDetailsForm/ExperimentDetailsValidation/experimentDetailsValidators";
import capitalize from "lodash/capitalize";
import BaseLabelRenderer from "../../FormComponents/BaseLabelRenderer";
import { EnableExtensionPrefixToggleLabel } from "../../ExperimentDetailsForm/ExperimentDetailsForm";
import { useBoolean } from "@fluentui/react-hooks";
import useAppInsights from "../../../../Hooks/UseAppInsights";
import debounce from "lodash/debounce";

interface VariantDetailsSidePanelProps {
  /**
   * Extension key to use as a prefix for variant names in order to ensure uniqueness
   */
  extensionPrefix: string;
  /**
   * Existing variant details for uniqueness validation
   */
  variantDetails?: VariantDetail[];
  /**
   * The variant being edited, or undefined for new variants
   */
  variantDetail?: VariantDetail;
  /**
   * List of all experiments for cross-experiment uniqueness validation
   */
  experiments: ExperimentType[];
  /**
   * When true, shows panel for control variant (vs treatment variant)
   */
  isControl: boolean;
  /**
   * When true, form will have pre-filled fields for cloning existing variant details
   */
  isClone?: boolean;
  /**
   * Callback for when the variant is saved
   */
  onSave: (variantDetail: VariantDetail) => void;
  /**
   * Callback for when the panel is dismissed
   */
  onDismiss: () => void;
}

/**
 * Default template for new variant details
 */
const defaultVariantDetail: VariantDetail = {
  featureFlags: {},
  name: "",
  isControl: false,
};

/**
 * Side panel for creating and editing experiment variants. Provides form fields
 * for variant name and feature flag configuration with validation.
 */
const VariantDetailPanel: React.FC<VariantDetailsSidePanelProps> = ({
  extensionPrefix,
  variantDetails,
  variantDetail,
  experiments,
  isControl,
  isClone,
  onSave,
  onDismiss,
}) => {
  const isEdit = !!variantDetail;
  const [saveWiggle, setSaveWiggle] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [enableExtensionPrefix, { toggle: toggleEnableExtensionPrefix }] =
    useBoolean(!!variantDetail?.name.startsWith(extensionPrefix));
  const { trackOperationEvent } = useAppInsights("VariantDetailPanel");

  const [variantDetailState, setVariantDetailState] = useState<VariantDetail>({
    ...defaultVariantDetail,
    ...variantDetail,
    name:
      // Remove extension prefix from name so prefix only shows in the prefix section
      variantDetail?.name.replace(new RegExp(`^${extensionPrefix}`), "") || "",
    featureFlags: { ...variantDetail?.featureFlags },
    isControl,
  });

  const hasChanged = useMemo(() => {
    if (!variantDetail) {
      return (
        variantDetailState.name !== "" ||
        Object.keys(variantDetailState.featureFlags).length > 0
      );
    }
    const adjustedVariantDetailName = variantDetail.name.replace(
      new RegExp(`^${extensionPrefix}`),
      "",
    );
    const nameChanged = adjustedVariantDetailName !== variantDetailState.name;
    const flagsChanged =
      JSON.stringify(variantDetail.featureFlags) !==
      JSON.stringify(variantDetailState.featureFlags);
    return nameChanged || flagsChanged;
  }, [
    extensionPrefix,
    variantDetail,
    variantDetailState.featureFlags,
    variantDetailState.name,
  ]);

  const onRemoveFlag = (flag: string) => {
    const { featureFlags } = variantDetailState;
    const featureFlagCopy = { ...featureFlags };
    trackOperationEvent({
      operation: "FormChange",
      function: "onRemoveFlag",
      properties: {
        field: "variantDetails.featureFlags",
        action: "remove",
        value: featureFlagCopy[flag],
        flag,
      },
    });
    delete featureFlagCopy[flag];
    setVariantDetailState((prev) => ({
      ...prev,
      featureFlags: { ...featureFlagCopy },
    }));
  };

  const validateUnique = useCallback(
    (fieldKey: string, fieldValue: any) => {
      const errors: string[] = [];
      const ignoreName = isEdit && !isClone ? variantDetail.name : undefined;
      const getUniqueSet = uniqueSets[fieldKey];
      if (getUniqueSet) {
        const uniqueSet = getUniqueSet(experiments);
        // Add prefix before comparing for cross-experiment uniqueness
        const valueToCompare =
          fieldKey === "name" ? `${extensionPrefix}${fieldValue}` : fieldValue;

        if (uniqueSet.has(valueToCompare) && valueToCompare !== ignoreName) {
          errors.push(
            `${capitalize(fieldKey)} must be unique across all experiments`,
          );
        }
      }
      const localNames = variantDetails?.map((variant) => variant.name) || [];
      if (localNames.includes(fieldValue) && fieldValue !== ignoreName) {
        errors.push(`${capitalize(fieldKey)} must be unique`);
      }
      return errors;
    },
    [
      isEdit,
      isClone,
      variantDetail?.name,
      variantDetails,
      experiments,
      extensionPrefix,
    ],
  );

  const validateForm = useCallback(
    (fieldKey?: string, fieldValue?: any, additionalParams?: any): boolean => {
      if (fieldKey && validators[fieldKey]) {
        const uniqueErrors = validateUnique(fieldKey, fieldValue);
        const fieldErrors = validators[fieldKey](fieldValue, additionalParams);
        const allErrors = [...fieldErrors, ...uniqueErrors];
        setErrors((prev) => ({
          ...prev,
          [fieldKey]: allErrors,
        }));
        return allErrors.length === 0;
      }

      // Validate all fields if no specific field is provided (e.g. on save)
      const formErrors: Record<string, string[]> = {};
      Object.entries(validators).forEach(([fieldKey, validator]) => {
        const value = variantDetailState[fieldKey as keyof VariantDetail];
        const fieldErrors =
          fieldKey === "name"
            ? validator(value, {
                enableExtensionPrefix,
                extensionPrefix,
              })
            : validator(value);
        const uniqueErrors = validateUnique(fieldKey, value);
        const allErrors = [...fieldErrors, ...uniqueErrors];
        if (allErrors.length > 0) {
          formErrors[fieldKey] = allErrors;
        }
      });
      setErrors(formErrors);
      return Object.keys(formErrors).length === 0;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ignore tracking
    [
      validateUnique,
      variantDetailState,
      enableExtensionPrefix,
      extensionPrefix,
    ],
  );

  const validateFormRef = useRef(validateForm);
  validateFormRef.current = validateForm;
  const debounceValidateAndTrack = useMemo(
    () =>
      debounce(
        (
          fieldKey: string,
          value: any,
          additionalParams?: any,
          initialValue?: any,
        ) => {
          const isValid = validateFormRef.current(
            fieldKey,
            value,
            additionalParams,
          );
          trackOperationEvent({
            operation: "FormChange",
            function: "handleFormChange",
            properties: {
              field: fieldKey,
              value,
              initialValue,
              additionalParams,
              isValid,
            },
          });
        },
        500,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- to avoid creating a new debounced function on every render
    [],
  );

  const handleFormChange = (
    fieldKey: string,
    value: any,
    additionalParams?: any,
  ) => {
    const initialValue = variantDetail
      ? variantDetail[fieldKey as keyof VariantDetail]
      : [];
    setVariantDetailState((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
    debounceValidateAndTrack(fieldKey, value, additionalParams, initialValue);
  };

  const handleOnSave = () => {
    if (validateForm()) {
      onSave({
        ...variantDetailState,
        // If enabled, prefix the variant name with the extension key to ensure uniqueness
        name: enableExtensionPrefix
          ? `${extensionPrefix}${variantDetailState.name}`
          : variantDetailState.name,
      });
    } else {
      setSaveWiggle(true);
      setTimeout(() => setSaveWiggle(false), 600);
    }
  };

  return (
    <Panel
      isOpen={true}
      type={PanelType.medium}
      onDismiss={onDismiss}
      isFooterAtBottom={true}
      isLightDismiss
      onRenderHeader={() => (
        <Stack className={sharedStyles.panelHeader}>
          <Label className={sharedStyles.panelHeaderLabel}>
            {isControl ? "Control " : "Treatment "} Variant Form
          </Label>
        </Stack>
      )}
      onRenderFooter={() => (
        <Stack
          horizontal
          horizontalAlign="end"
          className={styles.variantDetailPanelFooter}
        >
          <PrimaryButton
            text={isEdit ? "Update variant" : "Add variant"}
            onClick={handleOnSave}
            style={saveWiggle ? injectWiggleAnimation : undefined}
            disabled={!hasChanged}
          />
        </Stack>
      )}
    >
      <FluentProvider theme={webLightTheme}>
        <Stack tokens={{ childrenGap: 10 }}>
          {enableExtensionPrefix && (
            <MessageBar messageBarType={MessageBarType.info}>
              Extension prefix is enabled for this experiment. Make sure to
              include the prefix {`'${extensionPrefix}'`} when referencing the
              variable name in your code.
            </MessageBar>
          )}
          <FormRow horizontal>
            <BaseTextField
              fieldKey="name"
              label="Name"
              value={variantDetailState.name}
              isRequired
              isEdit={true}
              onChange={(_key, value) => {
                handleFormChange("name", value, {
                  enableExtensionPrefix: enableExtensionPrefix,
                  extensionPrefix,
                });
              }}
              errorMessage={errors.name}
              prefix={enableExtensionPrefix ? extensionPrefix : undefined}
            />
            <Toggle
              defaultChecked={enableExtensionPrefix}
              label={<EnableExtensionPrefixToggleLabel />}
              onText="enabled"
              offText="disabled"
              onChange={() => {
                validateForm("name", variantDetailState.name, {
                  enableExtensionPrefix: !enableExtensionPrefix,
                  extensionPrefix,
                });
                toggleEnableExtensionPrefix();
              }}
            />
          </FormRow>
          <Stack className={styles.variantDetailPanelFeatureFlagContainer}>
            <BaseLabelRenderer
              label="Feature Flags"
              isRequired
              description="Configuration for custom A/B and A/B/n experiments"
            />
            <FormRow
              className={styles.variantDetailPanelFeatureFlagFormContainer}
            >
              <BaseKeyValueField
                keyLabel="Feature Flag Name"
                valueLabel="Feature Flag Value"
                inferType={true}
                onInsert={(key, value) => {
                  const featureFlags = {
                    ...variantDetailState.featureFlags,
                    [key]: value,
                  };
                  handleFormChange("featureFlags", featureFlags);
                }}
                isRequired
                errorMessage={errors.featureFlags}
                disabled={
                  errors.featureFlags ? errors.featureFlags.length > 0 : false
                }
                validateKey={validators.featureFlagName}
              />
              <Stack style={{ width: "100%" }}>
                <Label>Preview Flags</Label>
                <Stack
                  horizontal
                  className={
                    styles.variantDetailPanelFeatureFlagsPreviewContainer
                  }
                >
                  {featureFlagRenderer(
                    variantDetailState.featureFlags,
                    onRemoveFlag,
                  )}
                </Stack>
              </Stack>
            </FormRow>
          </Stack>
        </Stack>
      </FluentProvider>
    </Panel>
  );
};

/**
 * Validation functions for variant fields
 */
const validators: Record<
  string,
  (value: any, additionalParams?: any) => string[]
> = {
  name: variantDetailNameValidator,
};

/**
 * Functions to generate unique value sets for cross-experiment validation
 */
const uniqueSets: Record<
  string,
  (experiments: ExperimentType[]) => Set<string>
> = {
  name: (experiments: ExperimentType[]) => {
    const variantDetails = experiments
      .flatMap((experiment) => experiment.variantDetails)
      .filter((variant): variant is VariantDetail => !!variant)
      .map((variant) => variant.name);
    return new Set(variantDetails);
  },
};

export default VariantDetailPanel;
